/**
 * Client-side helpers for digital listing media (metadata only — files live on Fast.io).
 */

/** Stable string id for API URLs and React keys (listing media ids may be UUID objects from Transit). */
export const digitalMediaIdStr = id => {
  if (id == null) {
    return '';
  }
  if (typeof id === 'string') {
    return id;
  }
  if (typeof id === 'object' && typeof id.uuid === 'string') {
    return id.uuid;
  }
  return String(id);
};

export const MAX_DIGITAL_MEDIA_ITEMS = 100;

export const MEDIA_SOURCE_FILE = 'file';
export const MEDIA_SOURCE_EXTERNAL = 'external';

export const PLAYBACK_MODE_EMBED = 'embed';
export const PLAYBACK_MODE_EXTERNAL = 'external';

export const ACCESS_LIMIT_NONE = 'none';
export const ACCESS_LIMIT_HOURS = 'hours';
export const ACCESS_LIMIT_DAYS = 'days';

export const isExternalMediaItem = item => {
  if (!item) {
    return false;
  }
  if (item.source === MEDIA_SOURCE_EXTERNAL) {
    return true;
  }
  if (item.externalUrl && !item.fastNodeId) {
    return true;
  }
  if (
    (item.playbackMode === PLAYBACK_MODE_EMBED || item.playbackMode === PLAYBACK_MODE_EXTERNAL) &&
    !item.fastNodeId
  ) {
    return true;
  }
  // Provider without a stored file indicates a hosted link saved before `source` existed.
  if (item.provider && !item.fastNodeId && !item.mimeType) {
    return true;
  }
  return false;
};

/** Unwrap `{ data: … }` API responses from jsonFetch. */
export const unwrapApiData = res =>
  res && typeof res === 'object' && res.data !== undefined && !Array.isArray(res.data)
    ? res.data
    : res;

/** Merge server playback payload onto a stored external item. */
export const mergeExternalPlaybackFields = (item, apiData = {}) => ({
  ...item,
  source: MEDIA_SOURCE_EXTERNAL,
  externalUrl: apiData.externalUrl || item?.externalUrl || null,
  embedUrl: apiData.embedUrl || item?.embedUrl || null,
  thumbnailUrl: apiData.thumbnailUrl || item?.thumbnailUrl || null,
  playbackMode: apiData.playbackMode || item?.playbackMode || PLAYBACK_MODE_EXTERNAL,
  provider: apiData.provider || item?.provider,
  providerLabel: apiData.providerLabel || item?.providerLabel,
  detectedType: apiData.detectedType || item?.detectedType,
  title: apiData.title || item?.title,
});

export const hasExternalPlaybackTarget = item => !!(item?.embedUrl || item?.externalUrl);

/** Open a hosted link in a new browser tab (used for non-embeddable external content). */
export const openExternalMediaUrl = url => {
  if (!url || typeof window === 'undefined') {
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const externalItemOpensInNewTab = item =>
  isExternalMediaItem(item) &&
  (item.playbackMode || PLAYBACK_MODE_EXTERNAL) !== PLAYBACK_MODE_EMBED &&
  !!item.externalUrl;

export const isFileMediaItem = item => !isExternalMediaItem(item);

export const resolveMediaPlaybackMode = item => {
  if (!item) {
    return null;
  }
  if (item.playbackMode === PLAYBACK_MODE_EMBED || item.playbackMode === PLAYBACK_MODE_EXTERNAL) {
    return item.playbackMode;
  }
  return isExternalMediaItem(item) ? PLAYBACK_MODE_EXTERNAL : null;
};

export const resolveMediaItemSource = item =>
  isExternalMediaItem(item) ? MEDIA_SOURCE_EXTERNAL : MEDIA_SOURCE_FILE;

export const normalizeAccessLimit = raw => {
  if (!raw || raw.type === 'none' || raw === ACCESS_LIMIT_NONE) {
    return { type: 'none' };
  }
  if (raw.type === 'hours' || raw.type === 'days') {
    const value = parseFloat(raw.value);
    if (Number.isNaN(value) || value <= 0) {
      return { type: 'none' };
    }
    return { type: raw.type, value };
  }
  return { type: 'none' };
};

export const detectTypeFromMime = mime => {
  if (!mime || typeof mime !== 'string') {
    return 'other';
  }
  const m = mime.toLowerCase();
  if (m.startsWith('video/')) {
    return 'video';
  }
  if (m.startsWith('audio/')) {
    return 'audio';
  }
  if (m.startsWith('image/')) {
    return 'image';
  }
  if (
    m === 'application/pdf' ||
    m.includes('document') ||
    m.includes('msword') ||
    m.includes('officedocument') ||
    m === 'text/plain'
  ) {
    return 'document';
  }
  return 'other';
};

export const shouldUseInlinePlayback = (mimeType, detectedType) => {
  const kind = detectedType || detectTypeFromMime(mimeType);
  return kind === 'video' || kind === 'audio';
};

/** Same inline-path rules as EditListingMediaPanel openMediaPreview. */
export const resolveInlineFileReadUrl = ({
  mimeType,
  detectedType,
  fastNodeId,
  inlinePath,
  apiReadUrl,
}) => {
  if (!fastNodeId || !inlinePath) {
    return apiReadUrl || null;
  }
  const kind = detectedType || detectTypeFromMime(mimeType);
  if (shouldUseInlinePlayback(mimeType, detectedType) || kind === 'image') {
    return inlinePath;
  }
  return apiReadUrl || null;
};

/** PDF / document iframe src — matches EditListingMediaPanel preview dialog. */
export const resolvePdfInlinePath = ({ mimeType, detectedType, inlinePath }) => {
  if (!inlinePath) {
    return null;
  }
  const m = mimeType || '';
  const dt = detectedType;
  if (m === 'application/pdf' || m.includes('pdf') || dt === 'document') {
    return inlinePath;
  }
  return null;
};

const DIGITAL_MEDIA_TYPE_ORDER = ['video', 'document', 'image', 'audio', 'link', 'other'];

/** Resolve content kind from stored item fields. */
export const resolveDigitalMediaItemType = item => {
  if (item?.detectedType) {
    return item.detectedType;
  }
  return detectTypeFromMime(item?.mimeType);
};

/**
 * Count digital media items by type (optionally filtered by section: content | teaser).
 * @returns {Record<string, number>}
 */
export const aggregateDigitalMediaByType = (items, { section } = {}) => {
  const counts = { video: 0, document: 0, image: 0, audio: 0, link: 0, other: 0 };
  (items || []).forEach(row => {
    if (!row) {
      return;
    }
    if (section && row.section !== section) {
      return;
    }
    if (isExternalMediaItem(row)) {
      counts.link += 1;
      return;
    }
    const kind = resolveDigitalMediaItemType(row);
    if (counts[kind] != null) {
      counts[kind] += 1;
    } else {
      counts.other += 1;
    }
  });
  return counts;
};

/** e.g. "3 videos, 2 documents, 10 images" — empty string when no items. */
export const formatDigitalMediaTypeCounts = (counts, intl) => {
  if (!counts || !intl) {
    return '';
  }
  const parts = DIGITAL_MEDIA_TYPE_ORDER.filter(kind => (counts[kind] || 0) > 0).map(kind =>
    intl.formatMessage({ id: `DigitalMedia.typeCount.${kind}` }, { count: counts[kind] })
  );
  return parts.join(' · ');
};

/** Summary of purchased/unlocked content items (section === content). */
export const formatDigitalContentSummary = (items, intl) => {
  const counts = aggregateDigitalMediaByType(items, { section: 'content' });
  return formatDigitalMediaTypeCounts(counts, intl);
};

export const formatMediaDurationSeconds = (seconds, intl) => {
  const n = Number(seconds);
  if (!intl || !Number.isFinite(n) || n <= 0) {
    return null;
  }
  if (n < 60) {
    return intl.formatMessage({ id: 'DigitalMedia.durationSeconds' }, { seconds: Math.round(n) });
  }
  const totalMinutes = Math.round(n / 60);
  if (totalMinutes < 60) {
    return intl.formatMessage({ id: 'DigitalMedia.durationMinutes' }, { minutes: totalMinutes });
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (minutes > 0) {
    return intl.formatMessage({ id: 'DigitalMedia.durationHoursMinutes' }, { hours, minutes });
  }
  return intl.formatMessage({ id: 'DigitalMedia.durationHours' }, { hours });
};

export const formatMediaPageCount = (pageCount, intl) => {
  const n = parseInt(pageCount, 10);
  if (!intl || !Number.isFinite(n) || n <= 0) {
    return null;
  }
  return intl.formatMessage({ id: 'DigitalMedia.pageCount' }, { count: n });
};

export const formatMediaResolution = (widthPx, heightPx, intl) => {
  const w = parseInt(widthPx, 10);
  const h = parseInt(heightPx, 10);
  if (!intl || !Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return null;
  }
  return intl.formatMessage({ id: 'DigitalMedia.resolution' }, { width: w, height: h });
};

/** Secondary line for cards: type label plus duration, pages, or resolution when available. */
export const formatDigitalMediaItemMeta = (item, intl) => {
  if (!item || !intl) {
    return '';
  }
  if (isExternalMediaItem(item)) {
    // Show length (for time-based media) or the media type. Unrecognized links show "Link".
    let kind = item.detectedType;
    // Legacy items: Drive /file/d/ recordings were sometimes misclassified as PDF.
    if (
      item.provider === 'google_drive' &&
      kind === 'document' &&
      (item.embedUrl || item.externalUrl || '').includes('/file/d/')
    ) {
      kind = 'video';
    }
    if (kind === 'video' || kind === 'audio') {
      const duration = formatMediaDurationSeconds(item.durationSeconds, intl);
      return duration || intl.formatMessage({ id: `DigitalMedia.typeLabel.${kind}` });
    }
    if (kind === 'document' || kind === 'image') {
      return intl.formatMessage({ id: `DigitalMedia.typeLabel.${kind}` });
    }
    return intl.formatMessage({ id: 'DigitalMedia.typeLabel.link' });
  }
  const kind = resolveDigitalMediaItemType(item);
  const typeLabel = intl.formatMessage({ id: `DigitalMedia.typeLabel.${kind}` });
  if (kind === 'video') {
    const duration = formatMediaDurationSeconds(item.durationSeconds, intl);
    return duration
      ? intl.formatMessage({ id: 'DigitalMedia.itemMeta.video' }, { type: typeLabel, duration })
      : typeLabel;
  }
  if (kind === 'audio') {
    const duration = formatMediaDurationSeconds(item.durationSeconds, intl);
    return duration
      ? intl.formatMessage({ id: 'DigitalMedia.itemMeta.audio' }, { type: typeLabel, duration })
      : typeLabel;
  }
  if (kind === 'document') {
    const pages = formatMediaPageCount(item.pageCount, intl);
    return pages
      ? intl.formatMessage({ id: 'DigitalMedia.itemMeta.document' }, { type: typeLabel, pages })
      : typeLabel;
  }
  if (kind === 'image') {
    const resolution = formatMediaResolution(item.widthPx, item.heightPx, intl);
    return resolution
      ? intl.formatMessage({ id: 'DigitalMedia.itemMeta.image' }, { type: typeLabel, resolution })
      : typeLabel;
  }
  return typeLabel;
};

export const ACCESS_LIMIT_MODE_PER_FILE = 'perFile';
export const ACCESS_LIMIT_MODE_GLOBAL = 'global';

export const getDigitalMediaAccessLimitMode = digitalMedia =>
  digitalMedia?.accessLimitMode === ACCESS_LIMIT_MODE_GLOBAL
    ? ACCESS_LIMIT_MODE_GLOBAL
    : ACCESS_LIMIT_MODE_PER_FILE;

export const getEffectiveItemAccessLimit = (item, digitalMedia) => {
  const mode = getDigitalMediaAccessLimitMode(digitalMedia);
  if (mode === ACCESS_LIMIT_MODE_GLOBAL) {
    return normalizeAccessLimit(digitalMedia?.globalAccessLimit);
  }
  return normalizeAccessLimit(item?.accessLimit);
};

export const listingHasLimitedAccess = digitalMedia => {
  const items = (digitalMedia?.items || []).filter(i => i && i.section === 'content');
  const mode = getDigitalMediaAccessLimitMode(digitalMedia);
  if (mode === ACCESS_LIMIT_MODE_GLOBAL) {
    return normalizeAccessLimit(digitalMedia?.globalAccessLimit).type !== 'none';
  }
  return items.some(item => normalizeAccessLimit(item?.accessLimit).type !== 'none');
};

export const formatAccessLimitShort = accessLimit => {
  const normalized = normalizeAccessLimit(accessLimit);
  if (normalized.type === 'none' || normalized.value == null) {
    return null;
  }
  if (normalized.type === ACCESS_LIMIT_HOURS) {
    return `${normalized.value}h`;
  }
  if (normalized.type === ACCESS_LIMIT_DAYS) {
    return `${normalized.value}d`;
  }
  return null;
};

export const MAX_MEDIA_DESCRIPTION_LENGTH = 300;

export const sanitizeMediaDescription = text => {
  if (text == null) {
    return '';
  }
  const trimmed = String(text).trim();
  if (!trimmed) {
    return '';
  }
  return trimmed.slice(0, MAX_MEDIA_DESCRIPTION_LENGTH);
};

const contentItemsOnly = items => (items || []).filter(i => i && i.section === 'content');

const itemIdKey = item => digitalMediaIdStr(item?.id);

export const diffDigitalMediaItems = (beforeItems, afterItems) => {
  const before = contentItemsOnly(beforeItems);
  const after = contentItemsOnly(afterItems);
  const beforeMap = new Map(before.map(i => [itemIdKey(i), i]));
  const afterMap = new Map(after.map(i => [itemIdKey(i), i]));

  const added = after.filter(i => !beforeMap.has(itemIdKey(i)));
  const removed = before.filter(i => !afterMap.has(itemIdKey(i)));

  const beforeOrder = before.map(itemIdKey);
  const afterOrder = after.map(itemIdKey);
  const sharedBefore = beforeOrder.filter(id => afterMap.has(id));
  const sharedAfter = afterOrder.filter(id => beforeMap.has(id));
  const reordered =
    sharedBefore.length > 1 &&
    sharedBefore.length === sharedAfter.length &&
    sharedBefore.some((id, idx) => id !== sharedAfter[idx]);

  const updated = after.filter(i => {
    const prev = beforeMap.get(itemIdKey(i));
    if (!prev) {
      return false;
    }
    return (
      (prev.title || '') !== (i.title || '') ||
      sanitizeMediaDescription(prev.description) !== sanitizeMediaDescription(i.description)
    );
  });

  return { added, removed, reordered, updated };
};

export const hasMaterialContentChanges = diff =>
  (diff?.added?.length || 0) > 0 || (diff?.removed?.length || 0) > 0 || diff?.reordered === true;

export const applyAddedAtOnPublish = (nextItems, publishedItems) => {
  const publishedIds = new Set((publishedItems || []).map(itemIdKey));
  const now = new Date().toISOString();
  return (nextItems || []).map(item => {
    const id = itemIdKey(item);
    if (!publishedIds.has(id) && !item.addedAt) {
      return { ...item, addedAt: now };
    }
    if (publishedIds.has(id) && item.addedAt) {
      return item;
    }
    if (publishedIds.has(id)) {
      const prev = (publishedItems || []).find(p => itemIdKey(p) === id);
      return prev?.addedAt ? { ...item, addedAt: prev.addedAt } : item;
    }
    return item;
  });
};

export const resolveMediaThumbnailSources = (item, paths = {}) => {
  const {
    cardPreviewPath,
    coverPreviewPath,
    externalThumbnailUrl,
    videoPosterSrc,
    documentPosterSrc,
  } = paths;
  if (item?.coverFastNodeId && coverPreviewPath) {
    return {
      thumbnailUrl: coverPreviewPath,
      fallbackThumbnailUrl: null,
      videoPosterSrc: null,
      documentPosterSrc: null,
    };
  }
  if (cardPreviewPath) {
    return {
      thumbnailUrl: cardPreviewPath,
      fallbackThumbnailUrl: null,
      videoPosterSrc: videoPosterSrc || null,
      documentPosterSrc: documentPosterSrc || null,
    };
  }
  if (externalThumbnailUrl) {
    return {
      thumbnailUrl: externalThumbnailUrl,
      fallbackThumbnailUrl: null,
      videoPosterSrc: null,
      documentPosterSrc: null,
    };
  }
  return {
    thumbnailUrl: null,
    fallbackThumbnailUrl: null,
    videoPosterSrc: videoPosterSrc || null,
    documentPosterSrc: documentPosterSrc || null,
  };
};

export const isMediaNewForUser = (item, viewedMap = {}) => {
  if (!item?.addedAt) {
    return false;
  }
  const listingId = item.listingId != null ? String(item.listingId) : null;
  const mediaId = itemIdKey(item);
  if (!listingId || !mediaId) {
    return false;
  }
  const viewedAt = viewedMap?.[listingId]?.[mediaId];
  if (!viewedAt) {
    return true;
  }
  return new Date(item.addedAt).getTime() > new Date(viewedAt).getTime();
};

export const formatContentChangeSummary = (diff, intl, listingTitle) => {
  const lines = [];
  (diff?.added || []).forEach(item => {
    lines.push(
      intl.formatMessage(
        { id: 'ContentUpdateNotify.addedLine' },
        { title: item.title || intl.formatMessage({ id: 'ContentUpdateNotify.untitled' }) }
      )
    );
  });
  (diff?.removed || []).forEach(item => {
    lines.push(
      intl.formatMessage(
        { id: 'ContentUpdateNotify.removedLine' },
        { title: item.title || intl.formatMessage({ id: 'ContentUpdateNotify.untitled' }) }
      )
    );
  });
  if (diff?.reordered) {
    lines.push(intl.formatMessage({ id: 'ContentUpdateNotify.reorderedLine' }));
  }
  const changeBlock = lines.length > 0 ? lines.map(l => `• ${l}`).join('\n') : '';
  return intl.formatMessage(
    { id: 'ContentUpdateNotify.messageTemplate' },
    { listingTitle: listingTitle || '', changes: changeBlock }
  );
};

export const formatAccessTimeRemaining = (expiresAt, intl) => {
  if (!intl || !expiresAt) {
    return null;
  }
  const expiry = expiresAt instanceof Date ? expiresAt : new Date(expiresAt);
  if (Number.isNaN(expiry.getTime())) {
    return null;
  }
  const diffMs = expiry.getTime() - Date.now();
  if (diffMs <= 0) {
    return intl.formatMessage({ id: 'LimitedAccessIndicator.expired' });
  }
  const totalMinutes = Math.ceil(diffMs / (60 * 1000));
  if (totalMinutes < 60) {
    return intl.formatMessage(
      { id: 'LimitedAccessIndicator.minutesLeft' },
      { count: totalMinutes }
    );
  }
  const totalHours = Math.ceil(totalMinutes / 60);
  if (totalHours < 48) {
    return intl.formatMessage({ id: 'LimitedAccessIndicator.hoursLeft' }, { count: totalHours });
  }
  const totalDays = Math.ceil(totalHours / 24);
  return intl.formatMessage({ id: 'LimitedAccessIndicator.daysLeft' }, { count: totalDays });
};
